import { useEffect, useState } from 'react';
import { useTheme, Tab, Tabs } from '@mui/material';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import {
  DEFAULT_TABLE_STYLES,
  DEFAULT_ADVANCE_BUTTON_STYLE,
  DEFAULT_TAB_SX_STYLES
} from '../../../../../utils/styleHelpers';
import {
  getWizardDataSubject,
  bulkValidate,
  getWizardDataLoadingSubject,
  wizardDataValidateResponse,
  bulkExport
} from '../../../../../store/ProvisionalAsset';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { getColumnDefsByKey } from '../../ColumnDefs/PreviewProvisionalAssetsDefs';

interface PreviewProvisionalAssetsStepProps {
  nextStep: () => void;
  title: string;
}

export interface TabProps {
  name: string;
  key: string;
}

const allTabs: TabProps[] = [
  { name: 'Preview', key: 'preview' },
  { name: 'Customer Errors', key: 'customerErrors' },
  { name: 'Product Errors', key: 'productErrors' },
  { name: 'Other Errors', key: 'otherErrors' }
];

export const PreviewProvisionalAssetsStep = ({ nextStep, title }: PreviewProvisionalAssetsStepProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepData, setStepData] = useState<wizardDataValidateResponse | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const theme = useTheme();

  const runBulkValidate = async () => {
    if (stepData?.preview && stepData.preview.length > 0) return;
    await bulkValidate();
  };

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const detectedSelectedTab = (newStepData: wizardDataValidateResponse) => {
    const indexTab = allTabs.findIndex((tab) => {
      return (newStepData[tab.key as keyof typeof newStepData] as [])?.length > 0;
    });

    return indexTab !== -1 ? indexTab : 0;
  };

  useEffect(() => {
    const wizardDataSub = getWizardDataSubject().subscribe((data) => {
      const newStepData = {
        customerErrors: data.customerErrors,
        errorCounts: data.errorCounts,
        otherErrors: data.otherErrors,
        preview: data.preview,
        productErrors: data.productErrors
      };
      setSelectedTab(detectedSelectedTab(newStepData));
      setStepData(newStepData);
    });

    const loadingSub = getWizardDataLoadingSubject().subscribe((loading) => {
      setIsLoading(loading);
    });

    runBulkValidate();

    return () => {
      if (wizardDataSub) wizardDataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 350px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  const submitStep = () => {
    nextStep();
  };

  const cardActions = () => (
    <div className="flex gap-3">
      {(stepData?.preview?.length || 0) > 0 && (
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={submitStep}
          data-cy="next-step-button"
          data-testid="next-step-button">
          Create Asset(s)
        </button>
      )}
      {(stepData?.errorCounts?.invalid || 0) > 0 && (
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={bulkExport}
          data-cy="export-errors-button"
          data-testid="export-errors-button">
          Export Errors
        </button>
      )}
    </div>
  );

  const infoText = () => {
    if (!stepData) return '';
    const messages = [];
    if (stepData.errorCounts?.invalid) {
      const count = stepData.errorCounts.invalid;
      messages.push(`⚠️ ${count} Invalid Error${count > 1 ? 's' : ''}`);
    }
    if (stepData.errorCounts?.valid) {
      const count = stepData.errorCounts.valid;
      messages.push(`${count} Valid Asset${count > 1 ? 's' : ''}`);
    }
    return messages.join(' | ');
  };

  const selectedTabKey = allTabs[selectedTab].key;
  const rows = stepData ? (stepData[selectedTabKey as keyof typeof stepData] as []) : [];
  const columns = getColumnDefsByKey(selectedTabKey);

  return (
    <PageCard
      dataTestId="preview-prov-assets-step"
      dataCy="preview-prov-assets-step"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title={title}
      cardActions={isLoading ? undefined : cardActions()}>
      <>
        {isLoading && (
          <div className="flex h-[10rem] m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {!isLoading && (
          <>
            <div className="px-4 pt-4 pb-3 text-sm">{infoText()}</div>
            <Tabs
              sx={DEFAULT_TAB_SX_STYLES}
              value={selectedTab}
              scrollButtons="auto"
              onChange={(_e, index) => {
                handleTabChange(index);
              }}
              slotProps={{
                indicator: {
                  sx: {
                    backgroundColor: theme.palette.primary.main,
                    height: 2,
                    borderRadius: 1
                  }
                }
              }}>
              {allTabs.map((tab, _index) => {
                const tabDataCount = stepData && (stepData[tab.key as keyof typeof stepData] as [])?.length;
                return (
                  <Tab
                    sx={{
                      color: 'grey9',
                      fontSize: '.8rem',
                      textTransform: 'none'
                    }}
                    key={tab.name}
                    label={`${tab.name} ${tabDataCount ? `(${tabDataCount})` : ''}`}
                  />
                );
              })}
            </Tabs>
            <BasicDataGrid
              key={selectedTab}
              rows={rows}
              columns={columns}
              sxStyle={sxStyle}
              disableColumnMenu
              showColumnVerticalBorder
              showCellVerticalBorder
            />
          </>
        )}
      </>
    </PageCard>
  );
};
